// angular
import {Component, computed, effect, inject, signal} from "@angular/core";
import {NgxSkeletonLoaderModule} from "ngx-skeleton-loader";
import {TranslateModule} from "@ngx-translate/core";
// material angular
import {MatDialog} from "@angular/material/dialog";
import {MatMenuModule} from "@angular/material/menu";
import {MatIconModule} from "@angular/material/icon";
import {MatBadgeModule} from "@angular/material/badge";
import {MatTabsModule} from "@angular/material/tabs";
// components
import {ReminderDetailsDialogComponent} from "@app/core/components/reminder-details-dialog/reminder-details-dialog.component";
import {NotificationItemComponent} from "./components/notification-item/notification-item.component";
// services
import {ReminderService} from "@app/+reminder/shared/services/reminder.service";
// constants
import {placeholderStyles} from "@app/shared/constants/placeholder-style";
import {IReminder} from "@app/shared/types/reminder.type";

@Component({
  selector: "app-notifications-menu",
  templateUrl: "./notifications-menu.component.html",
  styleUrls: ["./notifications-menu.component.scss"],
  standalone: true,
  imports: [
    TranslateModule,
    NgxSkeletonLoaderModule,
    MatMenuModule,
    MatIconModule,
    MatBadgeModule,
    MatTabsModule,
    NotificationItemComponent,
  ],
})
export class NotificationsMenuComponent {
  public placeholderStyles = placeholderStyles;

  // Dependencies
  private _reminderService = inject(ReminderService);
  private _dialog = inject(MatDialog);

  // Local state
  public currentDay = signal<"TODAY" | "TOMORROW">("TODAY");
  public currentTabIndex = signal<number>(0);

  // Signals from service
  public todayReminders: IReminder[];
  public tomorrowReminders: IReminder[];
  public unReadTodayRemindersCount: number;
  public unReadTomorrowRemindersCount: number;
  public isLoading: boolean;

  // Computed reminders based on current tab
  public reminders = computed(() => {
    const reminders =
      this.currentDay() === "TODAY"
        ? this.todayReminders
        : this.tomorrowReminders;

    return [...reminders].sort((a, b) => {
      if (a.seen === b.seen) return 0;
      return a.seen ? 1 : -1;
    });
  });

  constructor() {
    // Fetch data on initialization
    this._reminderService.getTodayReminders();
    this._reminderService.getTomorrowReminders();

    effect(() => {
      const data = this._reminderService.todayReminders();
      if (data) this.todayReminders = data;
    });

    effect(() => {
      const data = this._reminderService.tomorrowReminders();
      if (data) this.tomorrowReminders = data;
    });

    effect(() => {
      const data = this._reminderService.unReadTodayRemindersCount();
      if (data) this.unReadTodayRemindersCount = data;
    });

    effect(() => {
      const data = this._reminderService.unReadTomorrowRemindersCount();
      if (data) this.unReadTomorrowRemindersCount = data;
    });

    effect(() => {
      this.isLoading = this._reminderService.isRemindersLoading();
    });
  }

  public openDetailsDialog(reminderId?: string) {
    this._dialog.open<ReminderDetailsDialogComponent>(
      ReminderDetailsDialogComponent,
      {
        width: "100%",
        maxWidth: "500px",
        data: {reminderId, currentDay: this.currentDay()},
      }
    );
  }

  public onTabChange(selectedIndex: number) {
    this.currentTabIndex.set(selectedIndex);
    this.currentDay.set(selectedIndex === 1 ? "TOMORROW" : "TODAY");
  }

  public onOpenMenu() {
    // Reset to today's tab
    this.currentTabIndex.set(0);
    this.currentDay.set("TODAY");

    // Refresh data
    this._reminderService.getTodayReminders();
    this._reminderService.getTomorrowReminders();
  }

  public calculateRemindersCount() {
    const unReadReminders =
      (this.unReadTodayRemindersCount || 0) +
      (this.unReadTomorrowRemindersCount || 0);
    return unReadReminders !== 0 ? unReadReminders : null;
  }
}
